import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Fade,
  Zoom,
  useTheme,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider
} from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import { toast } from "react-toastify";
import { 
  Business, 
  Phone, 
  LocationOn, 
  Assignment, 
  PersonAdd, 
  Delete
} from '@material-ui/icons';
import ClientService from '../services/ClientService';
import TypeClientService from '../services/TypeClientService';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  fieldContainer: {
    marginBottom: theme.spacing(2),
  },
  submitButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    minWidth: 200,
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.4)',
    },
    '&:disabled': {
      background: '#ccc',
      boxShadow: 'none',
    },
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(1),
  },
  percentageIndicator: {
    marginTop: theme.spacing(1),
    borderRadius: 8,
  },
  beneficiaireCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  beneficiaireHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  addButton: {
    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #F57C00 0%, #EF6C00 100%)',
    },
  },
  deleteButton: {
    color: '#f44336',
  },
}));

const ClientForm = () => {
  const classes = useStyles();
  const theme = useTheme();
  const auth = useSelector((state) => state.auth);
  const [typesClient, setTypesClient] = useState([]);
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [showBeneficiaireForm, setShowBeneficiaireForm] = useState(false);
  const [currentBeneficiaire, setCurrentBeneficiaire] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    lien_parente: '',
    telephone: ''
  });

  const liensParente = [
    { value: 'EPOUX', label: 'Époux/Épouse' },
    { value: 'ENFANT', label: 'Enfant' },
    { value: 'PARENT', label: 'Parent' },
    { value: 'FRERE_SOEUR', label: 'Frère/Sœur' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await TypeClientService.getTypeClient();
        setTypesClient(response.data || []);
      } catch (error) {
        console.error('Erreur chargement types client:', error);
        toast.error("Erreur lors du chargement des types de client");
      }
    };
    fetchData();
  }, []);

  const initialValues = {
    idType: '',
    reference: '',
    nom: '',
    adresse: '',
    telephone: '',
    quota: '',
    remise: 0,
    tauxIpm: ''
  };

  const validationSchema = Yup.object().shape({
    idType: Yup.number().required("Type de client requis"),
    reference: Yup.string(),
    nom: Yup.string().required("Nom requis").min(2, "Le nom doit contenir au moins 2 caractères"),
    adresse: Yup.string().required("Adresse requise").min(5, "L'adresse doit contenir au moins 5 caractères"),
    telephone: Yup.string()
      .matches(/^[0-9+]+$/, "Numéro de téléphone invalide")
      .min(8, "Le téléphone doit avoir au moins 8 chiffres")
      .required("Téléphone requis"),
    quota: Yup.number().min(0, "Le quota ne peut pas être négatif"),
    remise: Yup.number().min(0, "Ne peut pas être négatif").max(100, "Ne peut pas dépasser 100%").default(0),
    tauxIpm: Yup.number().min(0, "Ne peut pas être négatif").max(100, "Ne peut pas dépasser 100%")
  });

  const calculateTotalPercentage = (remise, tauxIpm) => {
    return (parseFloat(remise) || 0) + (parseFloat(tauxIpm) || 0);
  };

  const getRemiseLabel = (idType) => {
    const labels = {
      1: 'Taux de remise (%)',
      2: 'Taux de participation client (%)'
    };
    return labels[idType] || 'Remise (%)';
  };

  const handleAddBeneficiaire = () => {
    if (!currentBeneficiaire.nom || !currentBeneficiaire.prenom || !currentBeneficiaire.lien_parente) {
      toast.error("Veuillez remplir les champs obligatoires du bénéficiaire");
      return;
    }

    const newBeneficiaire = {
      id: Date.now(), // ID temporaire pour la gestion frontend
      ...currentBeneficiaire
    };

    setBeneficiaires(prev => [...prev, newBeneficiaire]);
    setCurrentBeneficiaire({
      nom: '',
      prenom: '',
      date_naissance: '',
      lien_parente: '',
      telephone: ''
    });
    setShowBeneficiaireForm(false);
    toast.success("Bénéficiaire ajouté avec succès");
  };

  const handleRemoveBeneficiaire = (id) => {
    setBeneficiaires(prev => prev.filter(b => b.id !== id));
  };

  const getLienParenteLabel = (value) => {
    const lien = liensParente.find(l => l.value === value);
    return lien ? lien.label : value;
  };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const totalPercent = calculateTotalPercentage(values.remise, values.tauxIpm);
      
      if (totalPercent > 100) {
        toast.error(`Le pourcentage total (${totalPercent}%) dépasse 100%`);
        setSubmitting(false);
        return;
      }

      const clientData = {
        idType: values.idType,
        reference: values.reference,
        nom: values.nom,
        speudo: auth.nom,
        adresse: values.adresse.trim(),
        telephone: values.telephone,
        quota: values.quota,
        remise: values.remise,
        tauxIpm: values.tauxIpm,
        beneficiaires: beneficiaires.map(({ id, ...benef }) => benef) 
      };

      await ClientService.createClient(clientData);
      toast.success("Client créé avec succès");
      resetForm();
      setTotalPercentage(0);
      setBeneficiaires([]);
      
    } catch (error) {
      console.error('Erreur création client:', error);
      toast.error(error.response?.data || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={classes.formContainer}>
      <Formik 
        initialValues={initialValues} 
        validationSchema={validationSchema} 
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <Grid container spacing={3}>
              
              {/* Section Informations de base */}
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  Informations générales
                </Typography>
              </Grid>

              {/* Type de client */}
              <Grid item xs={12} md={6}>
                <Field name="idType">
                  {({ field, form }) => (
                    <TextField
                      {...field}
                      select
                      label="Type de client"
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={touched.idType && !!errors.idType}
                      helperText={touched.idType && errors.idType}
                      required
                      InputProps={{
                        startAdornment: <Business className={classes.iconAdornment} />
                      }}
                    >
                      <MenuItem value="">
                        <em>Sélectionnez un type</em>
                      </MenuItem>
                      {typesClient.map((type) => (
                        <MenuItem key={type.idType} value={type.idType}>
                          {type.designation}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Field>
              </Grid>

              {/* Référence */}
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="reference"
                  label="Référence"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.reference && !!errors.reference}
                  helperText={touched.reference && errors.reference}
                  InputProps={{
                    startAdornment: <Assignment className={classes.iconAdornment} />
                  }}
                />
              </Grid>

              {/* Nom */}
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="nom"
                  label="Nom complet"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.nom && !!errors.nom}
                  helperText={touched.nom && errors.nom}
                  required
                />
              </Grid>

              {/* Section Contact */}
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle} style={{ marginTop: theme.spacing(3) }}>
                  Informations de contact
                </Typography>
              </Grid>

              {/* Adresse */}
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="adresse"
                  label="Adresse"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.adresse && !!errors.adresse}
                  helperText={touched.adresse && errors.adresse}
                  required
                  InputProps={{
                    startAdornment: <LocationOn className={classes.iconAdornment} />
                  }}
                />
              </Grid>

              {/* Téléphone */}
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="telephone"
                  label="Téléphone"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.telephone && !!errors.telephone}
                  helperText={touched.telephone && errors.telephone}
                  required
                  InputProps={{
                    startAdornment: <Phone className={classes.iconAdornment} />
                  }}
                />
              </Grid>

              {/* Quota */}
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="quota"
                  label="Quota"
                  type="number"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.quota && !!errors.quota}
                  helperText={touched.quota && errors.quota}
                />
              </Grid>

              {/* Section Paramètres financiers (conditionnelle) */}
              {values.idType && (
                <Fade in={!!values.idType} timeout={500}>
                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle} style={{ marginTop: theme.spacing(3) }}>
                      Paramètres financiers
                    </Typography>
                  </Grid>
                </Fade>
              )}

              {values.idType && (
                <Zoom in={!!values.idType} style={{ transitionDelay: !!values.idType ? '200ms' : '0ms' }}>
                  <Grid container spacing={2}>
                    {/* Remise / Participation client */}
                    <Grid item xs={12} md={values.idType === '2' ? 6 : 12}>
                      <Field
                        as={TextField}
                        name="remise"
                        label={getRemiseLabel(values.idType)}
                        type="number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={touched.remise && !!errors.remise}
                        helperText={touched.remise && errors.remise}
                        onChange={(e) => {
                          setFieldValue("remise", e.target.value);
                          setTotalPercentage(calculateTotalPercentage(e.target.value, values.tauxIpm));
                        }}
                        inputProps={{ 
                          min: 0, 
                          max: 100,
                          step: 0.1 
                        }}
                      />
                    </Grid>

                    {/* Taux IPM (seulement pour type 2) */}
                    {values.idType === '2' && (
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          name="tauxIpm"
                          label="Taux de participation IPM (%)"
                          type="number"
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={touched.tauxIpm && !!errors.tauxIpm}
                          helperText={touched.tauxIpm && errors.tauxIpm}
                          onChange={(e) => {
                            setFieldValue("tauxIpm", e.target.value);
                            setTotalPercentage(calculateTotalPercentage(values.remise, e.target.value));
                          }}
                          inputProps={{ 
                            min: 0, 
                            max: 100,
                            step: 0.1 
                          }}
                        />
                      </Grid>
                    )}

                    {/* Indicateur de pourcentage total */}
                    {(values.remise || values.tauxIpm) && (
                      <Grid item xs={12}>
                        <Alert 
                          severity={totalPercentage > 100 ? "error" : totalPercentage === 100 ? "success" : "info"}
                          variant="outlined"
                          className={classes.percentageIndicator}
                          icon={false}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">
                              Pourcentage total: 
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {totalPercentage}%
                            </Typography>
                          </Box>
                          {totalPercentage > 100 && (
                            <Typography variant="caption" color="error">
                              Dépassement de 100% - Ajustez les valeurs
                            </Typography>
                          )}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Zoom>
              )}

              {/* Section Bénéficiaires */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography className={classes.sectionTitle} style={{ marginTop: theme.spacing(3), marginBottom: 0 }}>
                    <PersonAdd style={{ marginRight: theme.spacing(1) }} />
                    Bénéficiaires
                  </Typography>
                  <Button
                    startIcon={<PersonAdd />}
                    variant="contained"
                    className={classes.addButton}
                    onClick={() => setShowBeneficiaireForm(true)}
                    size="small"
                  >
                    Ajouter un bénéficiaire
                  </Button>
                </Box>

                {/* Formulaire d'ajout de bénéficiaire */}
                {showBeneficiaireForm && (
                  <Card className={classes.beneficiaireCard}>
                    <CardContent>
                      <div className={classes.beneficiaireHeader}>
                        <Typography variant="h6">Nouveau bénéficiaire</Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => setShowBeneficiaireForm(false)}
                        >
                          <Delete />
                        </IconButton>
                      </div>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Nom"
                            value={currentBeneficiaire.nom}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              nom: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Prénom"
                            value={currentBeneficiaire.prenom}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              prenom: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="Lien de parenté"
                            value={currentBeneficiaire.lien_parente}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              lien_parente: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            required
                          >
                            <MenuItem value="">
                              <em>Sélectionnez un lien</em>
                            </MenuItem>
                            {liensParente.map((lien) => (
                              <MenuItem key={lien.value} value={lien.value}>
                                {lien.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Téléphone"
                            value={currentBeneficiaire.telephone}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              telephone: e.target.value
                            }))}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Date de naissance"
                            type="date"
                            value={currentBeneficiaire.date_naissance}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              date_naissance: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddBeneficiaire}
                            fullWidth
                          >
                            Ajouter le bénéficiaire
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Liste des bénéficiaires ajoutés */}
                {beneficiaires.length > 0 && (
                  <Box className={classes.chipContainer}>
                    {beneficiaires.map((beneficiaire) => (
                      <Chip
                        key={beneficiaire.id}
                        label={`${beneficiaire.nom} ${beneficiaire.prenom} (${getLienParenteLabel(beneficiaire.lien_parente)})`}
                        onDelete={() => handleRemoveBeneficiaire(beneficiaire.id)}
                        color="primary"
                        variant="outlined"
                        deleteIcon={<Delete className={classes.deleteButton} />}
                      />
                    ))}
                  </Box>
                )}

                {beneficiaires.length === 0 && !showBeneficiaireForm && (
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: theme.spacing(2) }}>
                    Aucun bénéficiaire ajouté. Cliquez sur "Ajouter un bénéficiaire" pour en ajouter.
                  </Typography>
                )}
              </Grid>

              {/* Bouton de soumission */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" mt={4}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    className={classes.submitButton}
                    disabled={isSubmitting}
                    size="large"
                  >
                    {isSubmitting ? 'Création en cours...' : 'Créer le client'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ClientForm;